import React from "react";

/**
 * Extracts a YouTube video id from any of these URL variants:
 *   https://www.youtube.com/watch?v=ID
 *   https://youtu.be/ID
 *   https://www.youtube.com/embed/ID
 *   https://www.youtube.com/shorts/ID
 * Returns null if not YouTube.
 */
function extractYouTubeId(raw) {
  if (!raw) return null;
  try {
    const url = new URL(raw);
    if (url.hostname.includes("youtu.be")) {
      return url.pathname.slice(1).split(/[/?&]/)[0] || null;
    }
    if (url.hostname.includes("youtube.com")) {
      if (url.pathname === "/watch") return url.searchParams.get("v");
      const parts = url.pathname.split("/").filter(Boolean);
      // /embed/ID or /shorts/ID
      if (parts[0] === "embed" || parts[0] === "shorts") return parts[1] || null;
    }
    return null;
  } catch {
    return null;
  }
}

function isMp4(url) {
  return /\.(mp4|webm|ogg)(\?.*)?$/i.test(url || "");
}

/**
 * Renders a single homepage video (YouTube embed OR native <video>).
 * Fully responsive with 16:9 aspect ratio.
 */
export default function HomepageVideo({ url, title, subtitle }) {
  if (!url) return null;
  const ytId = extractYouTubeId(url);

  return (
    <div className="brutal-card overflow-hidden bg-black" data-testid="home-video">
      <div className="relative w-full" style={{ paddingBottom: "56.25%" }}>
        {ytId ? (
          <iframe
            className="absolute inset-0 w-full h-full"
            src={`https://www.youtube.com/embed/${ytId}?rel=0&modestbranding=1`}
            title={title || "Video"}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            allowFullScreen
          />
        ) : isMp4(url) ? (
          <video
            className="absolute inset-0 w-full h-full object-cover"
            src={url}
            controls
            playsInline
            preload="metadata"
          />
        ) : (
          // Fallback: treat as embeddable URL (Vimeo, etc.)
          <iframe
            className="absolute inset-0 w-full h-full"
            src={url}
            title={title || "Video"}
            allow="autoplay; fullscreen; picture-in-picture"
            allowFullScreen
          />
        )}
      </div>
      {(title || subtitle) && (
        <div className="p-4 bg-white">
          {title && <div className="font-display text-xl tracking-tight">{title}</div>}
          {subtitle && <div className="text-sm text-neutral-600 mt-1">{subtitle}</div>}
        </div>
      )}
    </div>
  );
}
